import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";
import { LogOut, Mail, Phone, Wallet, Trash2, Inbox, RefreshCw, Calendar, ExternalLink, Search, BarChart3, CheckCircle2, Users, Compass, UserCheck, UserPlus, StickyNote, Cake, Languages, Pencil, Check, X as XIcon, GitMerge, AlertTriangle, Loader2 } from "lucide-react";
import MailSeriesPanel from "../components/MailSeriesPanel";
import LeadFunnel from "../components/admin/LeadFunnel";
import AdminTopNav from "../components/AdminTopNav";
import { isAuthed, isAdmin, getAuthHeaders, logout as authLogout, loginUrl } from "../lib/adminAuth";
import { BACKEND_URL } from "../lib/apiBase";

const HEARD_FROM_LABELS = {
  google: "Google",
  referral: "Empfehlung",
  social_media: "Social Media",
  youtube: "YouTube",
  other: "Sonstiges",
};

const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const formatBirthdate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

// Ermüdungs-Stufen (backend/webinar_fatigue.py) — Ampel für Webinar-Mails.
const FATIGUE_BADGE = {
  full: {
    label: "Aktiv", bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500",
    panel: "bg-emerald-50 border-emerald-200 text-emerald-800",
    hint: "Volles Webinar-Mail-Programm (Einladung + 7-Tage + 24h + Verpasst).",
  },
  reduced: {
    label: "Reduziert", bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500",
    panel: "bg-amber-50 border-amber-200 text-amber-800",
    hint: "Bekommt nur noch den 24h-Last-Call.",
  },
  sparse: {
    label: "Selten", bg: "bg-orange-50", text: "text-orange-700", dot: "bg-orange-500",
    panel: "bg-orange-50 border-orange-200 text-orange-800",
    hint: "Bekommt den 24h-Last-Call nur noch bei jedem 2. Webinar.",
  },
  paused: {
    label: "Pausiert", bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500",
    panel: "bg-rose-50 border-rose-200 text-rose-800",
    hint: "Keine Webinar-Mails mehr — nur noch die Reaktivierung alle ~60 Tage.",
  },
};

export default function AdminDashboard() {
  const [contacts, setContacts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | new | contacted | customer
  // Inline-edit state for the lead/contact display name in the detail pane.
  const [editingName, setEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);
  // Inline-edit state for the e-mail address (admin-only, used to fix typos
  // and migrate orphaned mail history across leads/customers).
  const [editingEmail, setEditingEmail] = useState(false);
  const [editingEmailValue, setEditingEmailValue] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  const token = isAdmin() ? (typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null) : null;

  const fetchContacts = async () => {
    if (!isAuthed()) {
      navigate(loginUrl());
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/contacts`, {
        headers: getAuthHeaders(),
      });
      setContacts(res.data);
      if (res.data.length && !selected) setSelected(res.data[0]);
    } catch (err) {
      if (err?.response?.status === 401) {
        const target = loginUrl();
        authLogout();
        navigate(target);
      } else {
        toast.error("Fehler beim Laden der Anfragen.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // Load partners list for the manual attribution dropdown (admin only).
    if (isAdmin()) {
      axios
        .get(`${API}/admin/partners`, { headers: getAuthHeaders() })
        .then((r) => setPartners(r.data || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    const target = loginUrl();
    authLogout();
    navigate(target);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Diese Anfrage wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        headers: getAuthHeaders(),
      });
      const remaining = contacts.filter((c) => c.id !== id);
      setContacts(remaining);
      setSelected(remaining[0] || null);
      toast.success("Anfrage gelöscht.");
    } catch {
      toast.error("Konnte nicht gelöscht werden.");
    }
  };

  const filtered = contacts.filter((c) => {
    // Filters are 1:1 aligned with mail_group letters (A/B/C/D).
    if (statusFilter === "insider" && c.mail_group !== "A") return false;
    if (statusFilter === "new" && c.mail_group !== "B") return false;
    if (statusFilter === "contacted" && c.mail_group !== "C") return false;
    if (statusFilter === "customer" && c.mail_group !== "D") return false;
    if (!query) return true;
    return (c.name + " " + c.email + " " + (c.message || "")).toLowerCase().includes(query.toLowerCase());
  });

  const counts = {
    all: contacts.length,
    insider: contacts.filter((c) => c.mail_group === "A").length,
    new: contacts.filter((c) => c.mail_group === "B").length,
    contacted: contacts.filter((c) => c.mail_group === "C").length,
    customer: contacts.filter((c) => c.mail_group === "D").length,
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] overflow-x-hidden">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={LOGO_URL} alt="Alphalane" className="h-8 w-8 sm:h-9 sm:w-9 object-contain flex-shrink-0" />
            <div className="font-display min-w-0">
              <div className="font-semibold text-[14px] sm:text-[15px] truncate">
                <span className="al-grad-text">ALPHALANE</span>
                <span className="hidden sm:inline text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">Admin</span>
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            <button onClick={fetchContacts} data-testid="admin-refresh" aria-label="Aktualisieren" className="al-cta-ghost !py-2 !px-2 sm:!px-3 text-sm">
              <RefreshCw className="h-4 w-4" /> <span className="hidden sm:inline">Aktualisieren</span>
            </button>
            <button onClick={logout} data-testid="admin-logout" aria-label="Logout" className="al-cta-ghost !py-2 !px-2 sm:!px-3 text-sm">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <span className="al-tag">Inbox</span>
            <h1 className="mt-3 font-display font-bold text-2xl sm:text-3xl lg:text-4xl">
              Kontakt-Anfragen <span className="al-grad-text">({filtered.length})</span>
            </h1>
          </div>
          <div className="relative w-full sm:w-auto">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suchen…"
              className="pl-9 pr-4 py-2.5 rounded-full border border-[var(--al-line)] bg-white text-sm w-full sm:w-64 focus:outline-none focus:border-[var(--al-purple)]"
            />
          </div>
        </div>

        {/* Status filter pills — each tied to a mail-series group letter */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-status-filter">
          {[
            { id: "all", letter: null, label: "Alle", color: "slate" },
            { id: "insider", letter: "A", label: "Insider-Leads", color: "violet" },
            { id: "new", letter: "B", label: "Neu / unbearbeitet", color: "amber" },
            { id: "contacted", letter: "C", label: "Kontaktiert", color: "emerald" },
            { id: "customer", letter: "D", label: "Kunden", color: "purple" },
          ].map((f) => {
            const active = statusFilter === f.id;
            const colorMap = {
              slate: active ? "bg-slate-900 text-white border-transparent" : "border-[var(--al-line)] text-slate-600 hover:text-slate-900",
              amber: active ? "bg-amber-500 text-white border-transparent" : "border-[var(--al-line)] text-slate-600 hover:text-amber-700",
              emerald: active ? "bg-emerald-600 text-white border-transparent" : "border-[var(--al-line)] text-slate-600 hover:text-emerald-700",
              purple: active ? "al-grad-bg text-white border-transparent" : "border-[var(--al-line)] text-slate-600 hover:text-[var(--al-purple)]",
              violet: active ? "bg-violet-600 text-white border-transparent" : "border-[var(--al-line)] text-slate-600 hover:text-violet-700",
            };
            const letterBadgeMap = {
              slate: "bg-slate-200 text-slate-700",
              amber: "bg-amber-100 text-amber-800",
              emerald: "bg-emerald-100 text-emerald-800",
              purple: "bg-violet-100 text-violet-800",
              violet: "bg-violet-100 text-violet-800",
            };
            return (
              <button
                key={f.id}
                onClick={() => setStatusFilter(f.id)}
                data-testid={`admin-filter-${f.id}`}
                title={f.letter ? `Mail-Serie Gruppe ${f.letter}` : "Alle Anfragen"}
                className={`px-3 py-1.5 rounded-full border text-sm font-display font-semibold transition-all flex items-center gap-1.5 ${colorMap[f.color]}`}
              >
                {f.letter && (
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold tabular-nums ${active ? "bg-white/25 text-white" : letterBadgeMap[f.color]}`}
                  >
                    {f.letter}
                  </span>
                )}
                <span>{f.label}</span>
                <span className={`text-[11px] font-mono-tab font-bold tabular-nums ${active ? "opacity-90" : "text-slate-400"}`}>
                  {counts[f.id]}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="al-card p-12 text-center text-slate-500">Lade Anfragen…</div>
        ) : contacts.length === 0 ? (
          <div className="al-card p-12 text-center" data-testid="admin-empty">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-600">Noch keine Anfragen eingegangen.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-5">
            <div className="lg:col-span-5 al-card overflow-hidden" data-testid="admin-list">
              <ul className="divide-y divide-[var(--al-line)] max-h-[calc(100vh-260px)] overflow-y-auto">
                {filtered.map((c) => {
                  const isActive = selected?.id === c.id;
                  return (
                    <li
                      key={c.id}
                      data-testid={`admin-list-item-${c.id}`}
                      onClick={() => setSelected(c)}
                      className={`px-5 py-4 cursor-pointer transition-colors ${isActive ? "bg-[var(--al-grad-soft)] al-grad-soft-bg" : "hover:bg-slate-50"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-display font-semibold text-slate-900 truncate">{c.name}</div>
                            {c.mail_group === "D" && (
                              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full al-grad-bg text-white">Kunde</span>
                            )}
                            {c.mail_group === "C" && (
                              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Kontaktiert</span>
                            )}
                            {c.mail_group === "A" && (
                              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">Insider-Lead</span>
                            )}
                            {(!c.mail_group || c.mail_group === "B") && (
                              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">Neu</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate mt-0.5">{c.email}</div>
                        </div>
                        <div className="text-[11px] text-slate-400 whitespace-nowrap">
                          {formatDate(c.created_at)}
                        </div>
                      </div>
                      <p className="mt-2 text-[13px] text-slate-600 line-clamp-2">{c.message}</p>
                      {(c.heard_from || c.partner_name || c.partner_slug || c.birthdate) && (
                        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500 flex-wrap">
                          {c.heard_from && (
                            <>
                              <Compass className="h-3 w-3" />
                              <span>{HEARD_FROM_LABELS[c.heard_from] || c.heard_from}</span>
                            </>
                          )}
                          {c.heard_from === "referral" && c.referrer_name && (
                            <span className="text-[var(--al-purple)] font-semibold">· {c.referrer_name}</span>
                          )}
                          {(c.partner_name || c.partner_slug) && (
                            <span
                              className="inline-flex items-center gap-1 text-[var(--al-purple)] font-semibold"
                              data-testid={`admin-list-partner-${c.id}`}
                            >
                              <UserCheck className="h-3 w-3" />
                              Empfohlen durch:&nbsp;<span>{c.partner_name || c.partner_slug}</span>
                            </span>
                          )}
                          {c.birthdate && (
                            <span
                              className="inline-flex items-center gap-1 text-pink-600"
                              data-testid={`admin-list-birthdate-${c.id}`}
                            >
                              <Cake className="h-3 w-3" />
                              <span className="font-mono-tab">{formatBirthdate(c.birthdate)}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
                {filtered.length === 0 && (
                  <li className="p-8 text-center text-sm text-slate-500">Keine Treffer.</li>
                )}
              </ul>
            </div>

            <div className="lg:col-span-7">
              {selected ? (
                <div className="al-card p-7 sm:p-9" data-testid="admin-detail">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" /> {formatDate(selected.created_at)}
                      </div>
                      {editingName ? (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={editingNameValue}
                            onChange={(e) => setEditingNameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                (async () => {
                                  setSavingName(true);
                                  try {
                                    const r = await axios.patch(`${API}/admin/contacts/${selected.id}`, { name: editingNameValue.trim() }, { headers: getAuthHeaders() });
                                    setSelected(r.data);
                                    setContacts((cs) => cs.map((c) => (c.id === selected.id ? r.data : c)));
                                    toast.success("Name aktualisiert.");
                                    setEditingName(false);
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
                                  } finally {
                                    setSavingName(false);
                                  }
                                })();
                              }
                              if (e.key === "Escape") setEditingName(false);
                            }}
                            autoFocus
                            disabled={savingName}
                            placeholder="Name"
                            data-testid="admin-detail-name-input"
                            className="flex-1 min-w-[200px] max-w-md px-3 py-1.5 rounded-lg border border-violet-300 focus:outline-none focus:ring-2 focus:ring-[var(--al-purple)]/30 text-2xl sm:text-3xl font-display font-bold"
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              setSavingName(true);
                              try {
                                const r = await axios.patch(`${API}/admin/contacts/${selected.id}`, { name: editingNameValue.trim() }, { headers: getAuthHeaders() });
                                setSelected(r.data);
                                setContacts((cs) => cs.map((c) => (c.id === selected.id ? r.data : c)));
                                toast.success("Name aktualisiert.");
                                setEditingName(false);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
                              } finally {
                                setSavingName(false);
                              }
                            }}
                            disabled={savingName}
                            data-testid="admin-detail-name-save"
                            className="p-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                            title="Speichern"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingName(false)}
                            disabled={savingName}
                            data-testid="admin-detail-name-cancel"
                            className="p-2 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-700 disabled:opacity-50"
                            title="Abbrechen"
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <h2 className="font-display font-bold text-2xl sm:text-3xl" data-testid="admin-detail-name">{selected.name || "—"}</h2>
                          {selected.fatigue && FATIGUE_BADGE[selected.fatigue.stage] && (
                            <span
                              data-testid="admin-detail-fatigue-badge"
                              title={`Webinar-Ermüdung: ${selected.fatigue.ignored} Zyklen in Folge ignoriert — ${FATIGUE_BADGE[selected.fatigue.stage].hint}`}
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${FATIGUE_BADGE[selected.fatigue.stage].bg} ${FATIGUE_BADGE[selected.fatigue.stage].text}`}
                            >
                              <span className={`h-2 w-2 rounded-full ${FATIGUE_BADGE[selected.fatigue.stage].dot}`} />
                              {FATIGUE_BADGE[selected.fatigue.stage].label}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => { setEditingNameValue(selected.name || ""); setEditingName(true); }}
                            data-testid="admin-detail-name-edit"
                            title="Namen bearbeiten"
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => onDelete(selected.id)}
                      data-testid="admin-detail-delete"
                      className="al-cta-ghost !py-2 !px-3 text-sm !text-rose-600 hover:!border-rose-300 flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" /> Löschen
                    </button>
                  </div>

                  {selected.fatigue && selected.fatigue.stage !== "full" && FATIGUE_BADGE[selected.fatigue.stage] && (
                    <div
                      className={`mt-4 px-3.5 py-2.5 rounded-xl border text-xs leading-relaxed ${FATIGUE_BADGE[selected.fatigue.stage].panel}`}
                      data-testid="admin-detail-fatigue-panel"
                    >
                      <span className="font-bold uppercase tracking-widest text-[10px] mr-2">
                        Webinar-Ermüdung: {FATIGUE_BADGE[selected.fatigue.stage].label}
                      </span>
                      {selected.fatigue.ignored} Webinar-Zyklen in Folge ignoriert · {FATIGUE_BADGE[selected.fatigue.stage].hint} Öffnung, Klick oder Registrierung setzt die Stufe automatisch zurück.
                    </div>
                  )}

                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <Field icon={<Mail className="h-3.5 w-3.5" />} label="E-Mail">
                      {editingEmail ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <input
                            type="email"
                            value={editingEmailValue}
                            onChange={(e) => setEditingEmailValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                (async () => {
                                  const next = (editingEmailValue || "").trim().toLowerCase();
                                  if (!next || !next.includes("@")) {
                                    toast.error("Ungültige E-Mail-Adresse.");
                                    return;
                                  }
                                  if (next === (selected.email || "").toLowerCase()) {
                                    setEditingEmail(false);
                                    return;
                                  }
                                  if (!window.confirm(
                                    `E-Mail von "${selected.email}" auf "${next}" ändern?\n\n` +
                                    `→ Verknüpfte Leads, Kunden-Accounts und andere Kontaktanfragen ` +
                                    `mit der alten Adresse werden automatisch mit-umgehängt.`
                                  )) return;
                                  setSavingEmail(true);
                                  try {
                                    const r = await axios.patch(
                                      `${API}/admin/contacts/${selected.id}`,
                                      { email: next },
                                      { headers: getAuthHeaders() },
                                    );
                                    setSelected(r.data);
                                    setContacts((cs) => cs.map((c) => (c.id === selected.id ? r.data : c)));
                                    toast.success(`E-Mail aktualisiert auf ${next}.`);
                                    setEditingEmail(false);
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
                                  } finally {
                                    setSavingEmail(false);
                                  }
                                })();
                              }
                              if (e.key === "Escape") setEditingEmail(false);
                            }}
                            autoFocus
                            disabled={savingEmail}
                            placeholder="name@domain.com"
                            data-testid="admin-detail-email-input"
                            className="flex-1 min-w-[180px] px-2 py-1 rounded-md border border-violet-300 focus:outline-none focus:ring-2 focus:ring-[var(--al-purple)]/30 text-sm font-mono-tab"
                          />
                          <button
                            type="button"
                            onClick={async () => {
                              const next = (editingEmailValue || "").trim().toLowerCase();
                              if (!next || !next.includes("@")) {
                                toast.error("Ungültige E-Mail-Adresse.");
                                return;
                              }
                              if (next === (selected.email || "").toLowerCase()) {
                                setEditingEmail(false);
                                return;
                              }
                              if (!window.confirm(
                                `E-Mail von "${selected.email}" auf "${next}" ändern?\n\n` +
                                `→ Verknüpfte Leads, Kunden-Accounts und andere Kontaktanfragen ` +
                                `mit der alten Adresse werden automatisch mit-umgehängt.`
                              )) return;
                              setSavingEmail(true);
                              try {
                                const r = await axios.patch(
                                  `${API}/admin/contacts/${selected.id}`,
                                  { email: next },
                                  { headers: getAuthHeaders() },
                                );
                                setSelected(r.data);
                                setContacts((cs) => cs.map((c) => (c.id === selected.id ? r.data : c)));
                                toast.success(`E-Mail aktualisiert auf ${next}.`);
                                setEditingEmail(false);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen.");
                              } finally {
                                setSavingEmail(false);
                              }
                            }}
                            disabled={savingEmail}
                            data-testid="admin-detail-email-save"
                            className="p-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                            title="Speichern"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingEmail(false)}
                            disabled={savingEmail}
                            data-testid="admin-detail-email-cancel"
                            className="p-1.5 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-700 disabled:opacity-50"
                            title="Abbrechen"
                          >
                            <XIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <a href={`mailto:${selected.email}`} className="text-[var(--al-purple)] hover:underline inline-flex items-center gap-1" data-testid="admin-detail-email">
                            {selected.email} <ExternalLink className="h-3 w-3" />
                          </a>
                          <button
                            type="button"
                            onClick={() => { setEditingEmailValue(selected.email || ""); setEditingEmail(true); }}
                            data-testid="admin-detail-email-edit"
                            title="E-Mail bearbeiten"
                            className="p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </Field>
                    <Field icon={<Phone className="h-3.5 w-3.5" />} label="Telefon">
                      {selected.phone ? (
                        <a href={`tel:${selected.phone}`} className="text-slate-900">{selected.phone}</a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </Field>
                    <Field icon={<Wallet className="h-3.5 w-3.5" />} label="Investmentgröße">
                      {selected.investment_size ? <span className="text-slate-900 font-medium">{selected.investment_size}</span> : <span className="text-slate-400">—</span>}
                    </Field>
                    <Field icon={<span className="font-mono-tab text-[10px]">ID</span>} label="Anfrage-ID">
                      <span className="font-mono-tab text-xs text-slate-500">{selected.id.slice(0, 8)}…</span>
                    </Field>
                  </div>

                  <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    <Field icon={<Compass className="h-3.5 w-3.5" />} label="Wie gefunden">
                      {selected.heard_from ? (
                        <span className="text-slate-900 font-medium">{HEARD_FROM_LABELS[selected.heard_from] || selected.heard_from}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </Field>
                    <Field icon={<UserCheck className="h-3.5 w-3.5" />} label="Empfohlen durch">
                      <PartnerAttribution
                        selected={selected}
                        partners={partners}
                        onUpdate={async (slug) => {
                          try {
                            const payload = slug === null ? { clear_partner: true } : { partner_slug: slug };
                            const r = await axios.patch(`${API}/admin/contacts/${selected.id}`, payload, { headers: getAuthHeaders() });
                            setSelected((s) => ({ ...s, ...r.data }));
                            setContacts((arr) => arr.map((c) => (c.id === selected.id ? { ...c, ...r.data } : c)));
                            toast.success(slug ? "Partner zugeordnet." : "Partner-Zuordnung entfernt.");
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Konnte Partner nicht zuordnen.");
                          }
                        }}
                      />
                    </Field>
                  </div>

                  <div className="al-card p-4 sm:p-5 bg-gradient-to-br from-violet-50/50 to-white">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">Funnel-Status</div>
                    <LeadFunnel
                      emailHistory={selected.email_history || []}
                      customer={selected.customer || null}
                      createdAt={selected.created_at}
                    />
                  </div>

                  <MailSeriesPanel kind="contacts" contactId={selected.id} token={token} />

                  <DuplicateSuggestionsPanel
                    contact={selected}
                    token={token}
                    onMerged={({ deletedContactId }) => {
                      // The current contact was absorbed → remove from list + clear detail pane.
                      if (deletedContactId === selected.id) {
                        setContacts((cs) => cs.filter((c) => c.id !== deletedContactId));
                        setSelected(null);
                      } else {
                        fetchContacts();
                      }
                    }}
                  />

                  <ContactStatusEditor
                    key={selected.id}
                    contact={selected}
                    token={token}
                    onUpdated={(updated) => {
                      setContacts((cs) => cs.map((c) => (c.id === updated.id ? updated : c)));
                      setSelected(updated);
                    }}
                  />

                  <div className="mt-6 pt-6 border-t border-[var(--al-line)]">
                    <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-2">Nachricht</div>
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-3">
                    <a
                      href={`mailto:${selected.email}?subject=Re: Deine Anfrage bei Alphalane`}
                      className="al-cta"
                    >
                      Antworten <Mail className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="al-card p-12 text-center text-slate-500">
                  Wähle eine Anfrage aus der Liste.
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const Field = ({ icon, label, children }) => (
  <div className="rounded-xl border border-[var(--al-line)] p-3.5">
    <div className="flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-widest font-semibold">
      {icon} {label}
    </div>
    <div className="mt-1.5 text-sm">{children}</div>
  </div>
);

// Format ISO datetime → 'YYYY-MM-DD' for <input type="date">
const isoToDateInput = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const ContactStatusEditor = ({ contact, token, onUpdated }) => {
  const API_URL = `${BACKEND_URL}/api`;
  const [contacted, setContacted] = useState(!!contact.contacted_at);
  const [contactedDate, setContactedDate] = useState(isoToDateInput(contact.contacted_at));
  const [customer, setCustomer] = useState(!!contact.customer_since);
  const [customerDate, setCustomerDate] = useState(isoToDateInput(contact.customer_since));
  const [birthdate, setBirthdate] = useState(contact.birthdate || "");
  const [mailLanguage, setMailLanguage] = useState((contact.mail_language || "de").toLowerCase());
  const [notes, setNotes] = useState(contact.internal_notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const debounceRef = useRef(null);

  const patch = async (body) => {
    try {
      const res = await axios.patch(`${API_URL}/admin/contacts/${contact.id}`, body, { headers: getAuthHeaders() });
      onUpdated(res.data);
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    }
  };

  const toggleContacted = async () => {
    if (contacted) {
      setContacted(false);
      setContactedDate("");
      await patch({ clear_contacted: true });
      toast.success("Kontakt-Status entfernt.");
    } else {
      const todayIso = new Date().toISOString();
      setContacted(true);
      setContactedDate(isoToDateInput(todayIso));
      await patch({ contacted_at: todayIso });
      toast.success("Als kontaktiert markiert.");
    }
  };

  const toggleCustomer = async () => {
    if (customer) {
      setCustomer(false);
      setCustomerDate("");
      await patch({ clear_customer: true });
      toast.success("Kunde-Status entfernt.");
    } else {
      const todayIso = new Date().toISOString();
      setCustomer(true);
      setCustomerDate(isoToDateInput(todayIso));
      await patch({ customer_since: todayIso });
      toast.success("Als Kunde markiert.");
    }
  };

  const onDateChange = async (which, value) => {
    if (!value) return;
    const iso = new Date(`${value}T12:00:00Z`).toISOString();
    if (which === "contacted") {
      setContactedDate(value);
      await patch({ contacted_at: iso });
    } else {
      setCustomerDate(value);
      await patch({ customer_since: iso });
    }
  };

  const onBirthdateChange = async (value) => {
    setBirthdate(value);
    if (value) {
      await patch({ birthdate: value });
      toast.success("Geburtsdatum gespeichert.");
    } else {
      await patch({ clear_birthdate: true });
      toast.success("Geburtsdatum entfernt.");
    }
  };

  const onMailLanguageChange = async (value) => {
    const next = value === "en" ? "en" : "de";
    if (next === mailLanguage) return;
    setMailLanguage(next);
    await patch({ mail_language: next });
    toast.success(next === "en" ? "Mail-Sprache: Englisch" : "Mail-Sprache: Deutsch");
  };

  // Debounced auto-save for notes (1.2s after last keypress)
  useEffect(() => {
    if (notes === (contact.internal_notes || "")) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSavingNotes(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.patch(
          `${API_URL}/admin/contacts/${contact.id}`,
          { internal_notes: notes },
          { headers: getAuthHeaders() }
        );
        onUpdated(res.data);
      } catch {
        toast.error("Notiz speichern fehlgeschlagen.");
      } finally {
        setSavingNotes(false);
      }
    }, 1200);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  return (
    <div className="mt-6 pt-6 border-t border-[var(--al-line)]" data-testid="contact-status-editor">
      <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Status & Notizen</div>

      <div className="grid sm:grid-cols-2 gap-3">
        {/* Kontaktiert am */}
        <div className={`rounded-xl border p-3.5 transition-colors ${contacted ? "border-emerald-300 bg-emerald-50/40" : "border-[var(--al-line)]"}`}>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              data-testid="status-checkbox-contacted"
              type="checkbox"
              checked={contacted}
              onChange={toggleContacted}
              className="mt-0.5 h-4 w-4 accent-emerald-600 cursor-pointer flex-shrink-0"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-slate-700 text-[11px] uppercase tracking-widest font-semibold">
                <Phone className="h-3.5 w-3.5" /> Kontaktiert am
              </div>
              {contacted ? (
                <input
                  data-testid="status-date-contacted"
                  type="date"
                  value={contactedDate}
                  onChange={(e) => onDateChange("contacted", e.target.value)}
                  className="mt-1.5 w-full bg-white border border-[var(--al-line)] rounded-lg px-2.5 py-1.5 text-sm font-mono-tab focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              ) : (
                <div className="mt-1.5 text-xs text-slate-400">Noch nicht kontaktiert</div>
              )}
            </div>
          </label>
        </div>

        {/* Kunde seit */}
        <div className={`rounded-xl border p-3.5 transition-colors ${customer ? "border-[var(--al-purple)] bg-[var(--al-grad-soft)] al-grad-soft-bg" : "border-[var(--al-line)]"}`}>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              data-testid="status-checkbox-customer"
              type="checkbox"
              checked={customer}
              onChange={toggleCustomer}
              className="mt-0.5 h-4 w-4 accent-[var(--al-purple)] cursor-pointer flex-shrink-0"
            />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-slate-700 text-[11px] uppercase tracking-widest font-semibold">
                <UserPlus className="h-3.5 w-3.5" /> Kunde seit
              </div>
              {customer ? (
                <input
                  data-testid="status-date-customer"
                  type="date"
                  value={customerDate}
                  onChange={(e) => onDateChange("customer", e.target.value)}
                  className="mt-1.5 w-full bg-white border border-[var(--al-line)] rounded-lg px-2.5 py-1.5 text-sm font-mono-tab focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-purple-200"
                />
              ) : (
                <div className="mt-1.5 text-xs text-slate-400">Kein Kunde</div>
              )}
            </div>
          </label>
        </div>
      </div>

      {/* Geburtstag */}
      <div className={`mt-3 rounded-xl border p-3.5 transition-colors ${birthdate ? "border-pink-300 bg-pink-50/40" : "border-[var(--al-line)]"}`} data-testid="contact-birthdate-editor">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-slate-700 text-[11px] uppercase tracking-widest font-semibold">
            <Cake className="h-3.5 w-3.5 text-pink-500" /> Geburtstag
          </div>
          {birthdate && (
            <button
              type="button"
              onClick={() => onBirthdateChange("")}
              data-testid="contact-birthdate-clear"
              className="text-[11px] text-slate-400 hover:text-rose-600 transition-colors"
            >
              entfernen
            </button>
          )}
        </div>
        <input
          data-testid="contact-birthdate-input"
          type="date"
          value={birthdate}
          onChange={(e) => onBirthdateChange(e.target.value)}
          max={new Date().toISOString().slice(0, 10)}
          className="mt-1.5 w-full bg-white border border-[var(--al-line)] rounded-lg px-2.5 py-1.5 text-sm font-mono-tab focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-200"
        />
        <div className="mt-1.5 text-[11px] text-slate-400">
          Wird mit dem Kunden-Account (sofern vorhanden) synchronisiert.
        </div>
      </div>

      {/* Mail-Sprache */}
      <div
        className={`mt-3 rounded-xl border p-3.5 transition-colors ${mailLanguage === "en" ? "border-sky-300 bg-sky-50/40" : "border-[var(--al-line)]"}`}
        data-testid="contact-mail-language-editor"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-slate-700 text-[11px] uppercase tracking-widest font-semibold">
            <Languages className="h-3.5 w-3.5 text-sky-500" /> Mail-Sprache
          </div>
          <div className="inline-flex rounded-lg border border-[var(--al-line)] bg-white overflow-hidden text-xs font-semibold">
            <button
              type="button"
              data-testid="contact-mail-lang-de"
              onClick={() => onMailLanguageChange("de")}
              className={`px-3 py-1.5 transition-colors ${mailLanguage === "de" ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"}`}
            >
              DE
            </button>
            <button
              type="button"
              data-testid="contact-mail-lang-en"
              onClick={() => onMailLanguageChange("en")}
              className={`px-3 py-1.5 transition-colors ${mailLanguage === "en" ? "bg-sky-600 text-white" : "text-slate-500 hover:text-slate-700"}`}
            >
              EN
            </button>
          </div>
        </div>
        <div className="mt-1.5 text-[11px] text-slate-400">
          Englisch wird via KI on-demand übersetzt und gecached. Standard ist Deutsch.
        </div>
      </div>

      {/* Interne Notizen */}
      <div className="mt-3 rounded-xl border border-[var(--al-line)] p-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-slate-500 text-[11px] uppercase tracking-widest font-semibold">
            <StickyNote className="h-3.5 w-3.5" /> Interne Notizen
          </div>
          <span className="text-[10px] text-slate-400 font-medium">
            {savingNotes ? "Speichere…" : (notes ? "Auto-gespeichert" : "")}
          </span>
        </div>
        <textarea
          data-testid="status-notes"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Frei für eigene Notizen — z.B. Telefonat-Zusammenfassung, nächste Schritte, Fragen…"
          className="mt-2 w-full bg-white border border-[var(--al-line)] rounded-lg px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:border-[var(--al-purple)] focus:ring-2 focus:ring-purple-200 resize-none"
        />
      </div>
    </div>
  );
};


// Admin-only widget: shows the currently attributed partner with an inline
// dropdown to manually re-assign or clear the attribution. Useful for
// orphaned referrals (e.g. "Pascal (kein Match in DB)") where the lead
// typed a partner name into the contact form but no partner matched.
function PartnerAttribution({ selected, partners, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const currentSlug = selected.partner_slug || "";
  const currentName = selected.partner_name || selected.partner_slug;
  const hasMatch = Boolean(selected.partner_id);
  const referrerHint =
    selected.heard_from === "referral" && selected.referrer_name && !hasMatch
      ? selected.referrer_name
      : null;

  if (!isAdmin()) {
    // Read-only fallback for non-admin sessions
    if (currentName) {
      return (
        <span className="text-[var(--al-purple)] font-semibold" data-testid="admin-detail-partner">
          {currentName}
        </span>
      );
    }
    return referrerHint
      ? <span className="text-[var(--al-purple)] font-semibold">{referrerHint} (kein Match in DB)</span>
      : <span className="text-slate-400">—</span>;
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-detail-partner">
        {hasMatch ? (
          <span className="text-[var(--al-purple)] font-semibold">
            {currentName}
            {selected.partner_slug && selected.partner_name && (
              <span className="ml-2 text-[10px] font-mono-tab text-slate-400">({selected.partner_slug})</span>
            )}
          </span>
        ) : referrerHint ? (
          <span className="text-amber-700 font-semibold">{referrerHint} <span className="text-amber-500 font-normal text-[11px]">(kein Match in DB)</span></span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid="edit-partner-attribution"
          className="text-[10px] uppercase tracking-widest font-semibold text-violet-600 hover:text-violet-800 border border-violet-200 hover:bg-violet-50 rounded-full px-2 py-0.5 transition-colors"
        >
          Bearbeiten
        </button>
      </div>
    );
  }

  const sorted = [...partners].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de"));
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="admin-detail-partner-editor">
      <select
        defaultValue={currentSlug}
        data-testid="partner-attribution-select"
        onChange={async (e) => {
          const v = e.target.value;
          if (v === "__clear__") {
            await onUpdate(null);
          } else if (v) {
            await onUpdate(v);
          }
          setEditing(false);
        }}
        className="bg-white border border-violet-200 rounded-md text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-violet-200"
      >
        <option value="">— Partner auswählen —</option>
        {sorted.map((p) => (
          <option key={p.id} value={p.slug}>
            {p.name} ({p.slug})
          </option>
        ))}
        {hasMatch && <option value="__clear__">✕ Zuordnung entfernen</option>}
      </select>
      <button
        type="button"
        onClick={() => setEditing(false)}
        className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 hover:text-slate-700"
      >
        Abbrechen
      </button>
    </div>
  );
}


// ===== Duplicate Suggestions Panel =====
// Surfaces other rows (across leads/contacts/customers/partners) that probably
// refer to the same physical person. Triggered lazily by clicking "Duplikate
// prüfen" so the admin dashboard doesn't pay the O(n) scan on every render.
function DuplicateSuggestionsPanel({ contact, token, onMerged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null); // null | {suggestions: [...]}
  const [merging, setMerging] = useState(null); // id of suggestion currently merging
  const [err, setErr] = useState("");
  // Manual fallback: paste an e-mail of another record to merge with.
  const [manualEmail, setManualEmail] = useState("");
  const [manualBusy, setManualBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(
        `${API}/admin/contacts/${contact.id}/duplicate-suggestions`,
        { headers: getAuthHeaders() },
      );
      setData(r.data);
      const n = (r.data?.suggestions || []).length;
      if (n === 0) {
        toast.success("Keine Duplikate gefunden — Datensatz sieht sauber aus.");
      } else {
        toast.message(`${n} mögliche Duplikat${n === 1 ? "" : "e"} gefunden.`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Suche fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  // Reset on contact change
  useEffect(() => {
    setData(null);
    setErr("");
    setManualEmail("");
  }, [contact.id]);

  const mergeOne = async (s) => {
    // Strategy:
    //  * If the suggestion is a CUSTOMER → absorb THIS contact (lead-style)
    //    into the customer using /maintenance/absorb-into-customer.
    //  * If THIS row IS a customer (selected.customer attached) and suggestion
    //    is a lead/contact → absorb suggestion into the customer.
    //  * Otherwise (lead↔lead / contact↔contact / lead↔contact): simplest
    //    safe path is to delete the duplicate after stamping a confirmation,
    //    since both are still in the funnel and no customer record exists.
    const baseLabel = `${contact.name || "Unbekannt"} <${contact.email || "—"}>`;
    const dupLabel = `${s.name || "Unbekannt"} <${s.email || "—"}> [${s.kind}]`;

    if (s.kind === "customer") {
      if (!window.confirm(
        `MERGE: diesen Kontakt (${baseLabel}) in den Kunden ${dupLabel} einbinden?\n\n` +
        `→ Mail-Historie wird in den Kunden übernommen.\n` +
        `→ Diese Kontaktanfrage wird anschließend gelöscht.\n` +
        `→ Vorgang ist NICHT rückgängig zu machen.`
      )) return;
      setMerging(s.id);
      try {
        const r = await axios.post(
          `${API}/admin/maintenance/absorb-into-customer`,
          {
            customer_id: s.id,
            source_collection: "contact_requests",
            source_id: contact.id,
          },
          { headers: getAuthHeaders() },
        );
        toast.success(
          `Eingebunden. ${r.data.email_history_merged} Mail(s) übernommen.`,
        );
        onMerged && onMerged({ deletedContactId: contact.id, customerEmail: r.data.customer_email });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Merge fehlgeschlagen.");
      } finally {
        setMerging(null);
      }
      return;
    }

    if (s.kind === "lead" || s.kind === "contact") {
      // Try to find a customer this contact is already linked to via selected.customer
      const linkedCustomer = contact.customer;
      if (linkedCustomer && linkedCustomer.id) {
        if (!window.confirm(
          `MERGE: ${dupLabel} in den verknüpften Kunden ${linkedCustomer.email} einbinden?\n\n` +
          `→ Mail-Historie wird in den Kunden übernommen.\n` +
          `→ Dieser Lead/Contact wird anschließend gelöscht.`
        )) return;
        setMerging(s.id);
        try {
          const r = await axios.post(
            `${API}/admin/maintenance/absorb-into-customer`,
            {
              customer_id: linkedCustomer.id,
              source_collection: s.collection,
              source_id: s.id,
            },
            { headers: getAuthHeaders() },
          );
          toast.success(
            `Eingebunden. ${r.data.email_history_merged} Mail(s) übernommen.`,
          );
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Merge fehlgeschlagen.");
        } finally {
          setMerging(null);
        }
        return;
      }
      // No customer to anchor to — just offer to delete the duplicate row.
      if (!window.confirm(
        `Kein zugehöriger Kunden-Account vorhanden — soll ${dupLabel} einfach gelöscht werden?\n\n` +
        `Die Mail-History dieses Eintrags geht damit verloren.`
      )) return;
      setMerging(s.id);
      try {
        if (s.collection === "leads") {
          // Leads müssen über die contact_requests DELETE umgehen, da Leads
          // i.d.R. mit einer contact_request verknüpft sind. Fallback: direkter
          // DELETE auf /admin/contacts wenn die Source eine contact_request ist.
          await axios.delete(`${API}/admin/leads/${s.id}`, { headers: getAuthHeaders() });
        } else {
          await axios.delete(`${API}/admin/contacts/${s.id}`, { headers: getAuthHeaders() });
        }
        toast.success("Duplikat gelöscht.");
        await load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen.");
      } finally {
        setMerging(null);
      }
      return;
    }

    if (s.kind === "partner") {
      toast.message("Partner-Merges müssen manuell vorgenommen werden — nutze Admin → Partner.");
      return;
    }
  };

  const mergeByEmail = async () => {
    const target = (manualEmail || "").trim().toLowerCase();
    if (!target || !target.includes("@")) {
      toast.error("Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    if (target === (contact.email || "").toLowerCase()) {
      toast.error("Das ist dieselbe E-Mail wie der aktuelle Eintrag.");
      return;
    }
    if (!window.confirm(
      `MERGE: Eintrag mit E-Mail "${target}" mit diesem Kontakt (${contact.email}) zusammenführen?\n\n` +
      `→ Wenn der andere ein Kunde ist, wird dieser Eintrag dort eingebunden.\n` +
      `→ Wenn beides Leads/Anfragen sind, wird der andere gelöscht und seine Mail-Historie übernommen.\n` +
      `→ Vorgang ist NICHT rückgängig zu machen.`
    )) return;
    setManualBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/contacts/${contact.id}/merge-with-email`,
        { other_email: target },
        { headers: getAuthHeaders() },
      );
      const d = r.data;
      const moved = d.email_history_merged || 0;
      if (d.strategy === "absorb_into_customer") {
        toast.success(
          `Eingebunden in Kunden ${d.customer_email}. ${moved} Mail(s) übernommen.`,
        );
        // If the current row got absorbed → bubble up so list resets.
        if (d.source_id === contact.id) {
          onMerged && onMerged({ deletedContactId: contact.id, customerEmail: d.customer_email });
        } else {
          await load();
        }
      } else if (d.strategy === "kept_base_dropped_other") {
        toast.success(
          `Zusammengeführt. ${moved} Mail(s) übernommen. ${d.dropped_email} wurde gelöscht.`,
        );
        await load();
      }
      setManualEmail("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manueller Merge fehlgeschlagen.");
    } finally {
      setManualBusy(false);
    }
  };

  const suggestions = data?.suggestions || [];

  return (
    <div className="mt-6 al-card p-4 sm:p-5 border border-amber-100">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <GitMerge className="h-4 w-4 text-amber-600" />
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
            Duplikat-Prüfung
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="dup-check-trigger"
          className="al-cta-ghost !py-1.5 !px-3 text-xs"
        >
          {loading ? (
            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Suche …</>
          ) : data ? (
            <><RefreshCw className="h-3.5 w-3.5" /> Erneut prüfen</>
          ) : (
            <><Search className="h-3.5 w-3.5" /> Duplikate prüfen</>
          )}
        </button>
      </div>
      <div className="text-xs text-slate-500 mb-3">
        Sucht nach gleichem Namen, gleicher Telefonnummer oder ähnlicher E-Mail in allen Collections.
      </div>

      {/* Manual merge fallback — use when the fuzzy finder misses a pair
          (e.g. completely different names, no shared phone). */}
      <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">
          Manuell zusammenführen
        </div>
        <div className="text-xs text-slate-500 mb-2">
          E-Mail-Adresse des anderen Eintrags eingeben — wir finden ihn in Leads, Anfragen oder Kunden-Accounts und führen ihn mit diesem Kontakt zusammen.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="email"
            value={manualEmail}
            onChange={(e) => setManualEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") mergeByEmail(); }}
            disabled={manualBusy}
            placeholder="z.B. hahnewald@aol.com"
            data-testid="manual-merge-input"
            className="flex-1 min-w-[200px] px-3 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[var(--al-purple)]/30 text-sm font-mono-tab"
          />
          <button
            type="button"
            onClick={mergeByEmail}
            disabled={manualBusy || !manualEmail.trim()}
            data-testid="manual-merge-btn"
            className="al-cta-primary !py-1.5 !px-3 text-xs disabled:opacity-50"
          >
            {manualBusy ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Merge …</>
            ) : (
              <><GitMerge className="h-3.5 w-3.5" /> Mit E-Mail mergen</>
            )}
          </button>
        </div>
      </div>

      {err && (
        <div className="flex items-start gap-2 p-2.5 rounded-md bg-rose-50 text-rose-700 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>{err}</div>
        </div>
      )}

      {data && suggestions.length === 0 && (
        <div className="text-sm text-slate-500 italic">Keine Duplikate gefunden.</div>
      )}

      {suggestions.length > 0 && (
        <div className="space-y-2" data-testid="dup-suggestions-list">
          {suggestions.map((s) => (
            <div
              key={`${s.collection}-${s.id}`}
              data-testid={`dup-suggestion-${s.kind}-${s.id}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-amber-50/60 border border-slate-200 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                    {s.kind}
                  </span>
                  <span className="font-semibold text-slate-900 truncate">{s.name || "—"}</span>
                  <span className="text-xs text-slate-400">Score {s.score}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1 font-mono-tab truncate">
                  {s.email || "—"}
                  {s.phone ? <span className="ml-2">· {s.phone}</span> : null}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(s.reasons || []).map((r) => (
                    <span
                      key={r}
                      className="text-[10px] uppercase tracking-wider text-slate-500 bg-white border border-slate-200 rounded px-1.5 py-0.5"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => mergeOne(s)}
                disabled={merging === s.id || s.kind === "partner"}
                data-testid={`dup-merge-btn-${s.id}`}
                className="al-cta-primary !py-1.5 !px-3 text-xs disabled:opacity-50"
                title={s.kind === "partner"
                  ? "Partner-Merges müssen manuell erfolgen"
                  : "Zusammenführen / einbinden"}
              >
                {merging === s.id ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> …</>
                ) : (
                  <><GitMerge className="h-3.5 w-3.5" /> Mergen</>
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

